/**
 * Theme store (day/night) — Zustand.
 *
 * Persists choice to localStorage and reflects the value on the <html>
 * element via `data-theme="light"|"dark"` so all CSS variables in
 * theme-tokens.css can flip automatically.
 */
import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'pm-theme';

function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  // Fallback to OS preference
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToDom(theme: ThemeMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // Also help native form controls / scrollbars
  document.documentElement.style.colorScheme = theme;
}

interface ThemeState {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const initial = getInitialTheme();
  // Apply ASAP so first paint is correct
  applyToDom(initial);

  return {
    theme: initial,
    setTheme: (t) => {
      window.localStorage.setItem(STORAGE_KEY, t);
      applyToDom(t);
      set({ theme: t });
    },
    toggle: () => {
      const next: ThemeMode = get().theme === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      applyToDom(next);
      set({ theme: next });
    },
  };
});

